import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { notify } from '@yembo/yemblocks-core';
import { CUSTOMIZATION_QUERY_KEY, LIST_KEY, POST_KEY, PUT_KEY } from 'src/queries/queryKeys';
import { getStatuses } from 'src/queries/apiStatus';
import {
  invalidateIfAllMutationsDone,
  queryYemboApiCall,
  queryYemboApiCallWithReturnValue,
} from 'src/queries/queryYemboApiCalls';
import { MutationError } from 'src/queries/utils';

export type CustomizationItemPrompt = 'crating';
export type CustomizationRoomPrompt = 'floor';
export type CustomizationHelpContactOptionType = 'phone' | 'email';

export type CustomizationRedactionRuleType = {
  statuses: string[];
  olderThan: number;
};

export type CustomizationScreen =
  | 'csat'
  | 'stops'
  | 'job_date'
  | 'moving_to'
  | 'moving_from'
  | 'questionnaire'
  | 'specialty_items'
  | 'previous_job_weight';

export type CustomizationSpecialtyItemType =
  | 'firearm'
  | 'frontload washer'
  | 'dryer'
  | 'large tv'
  | 'small vehicle'
  | 'pool table'
  | 'grandfather clock'
  | 'hot tub'
  | 'piano'
  | 'small vessel'
  | 'boat'
  | 'animal house'
  | 'gym'
  | 'safe'
  | 'existing crates'
  | 'specialty beds'
  | 'valuable wall art';

export type Customization = {
  key: string;
  companyKey: string;
  requireVideo: boolean | null;
  screens: CustomizationScreen[] | null;
  shouldAllowEmptyDestination?: boolean;
  shouldAllowCustomItems: boolean | null;
  shouldHideArrivalTimes?: boolean;
  isCaPrivacyRightsEnabled: boolean | null;
  roomPrompts: CustomizationRoomPrompt[] | null;
  itemPrompts: CustomizationItemPrompt[] | null;
  specialtyItemTypes: CustomizationSpecialtyItemType[] | null;
  helpContactOptions: CustomizationHelpContactOptionType[] | null;
  skipAnalysisRooms?: string[] | null;
  shouldBlockDuplicateMerging?: boolean;
  redactionRules?: CustomizationRedactionRuleType[] | [];
};

const url = 'customization';

export const getCustomizations = async (companyKey: string) =>
  (await queryYemboApiCallWithReturnValue<Customization[], 'customizations'>({
    url,
    key: 'customizations',
    urlParams: new URLSearchParams({ companyKey }),
    shouldNotifyAndThrowError: true,
  })) ?? [];

type UseCustomizationOptions = Partial<UseQueryOptions<Customization[]>>;

export const useCustomizationsQuery = (companyKey: string | undefined, options?: UseCustomizationOptions) =>
  useQuery({
    ...options,
    queryKey: [CUSTOMIZATION_QUERY_KEY, LIST_KEY, companyKey],
    queryFn: () => getCustomizations(companyKey ?? ''),
    enabled: !!companyKey && options?.enabled !== false,
  });

export const useCustomizationsQueries = (companyKeys: string[], options?: UseCustomizationOptions) =>
  useQueries({
    queries: companyKeys.map((key) => ({
      ...options,
      enabled: !!key && options?.enabled !== false,
      queryKey: [CUSTOMIZATION_QUERY_KEY, LIST_KEY, key],
      queryFn: () => getCustomizations(key ?? ''),
    })),
    combine: (results) => ({
      data: results.map((result) => result.data?.[0]),
      error: results.find((result) => result.error),
      isLoading: results.some((result) => result.isLoading),
    }),
  });

const putCustomization = async (customization: Partial<Customization>) => {
  const response = await queryYemboApiCall({
    url,
    method: 'PUT',
    body: JSON.stringify(customization),
  });

  if (!response.ok) {
    const { message = 'Company updating has failed', type } = getStatuses(await response.json())[0] ?? {};

    throw new MutationError(message, type);
  }
};

type PartialCustomization = Partial<Customization> & Pick<Customization, 'key' | 'companyKey'>;
type PutCustomizationOptions = UseMutationOptions<void, MutationError, PartialCustomization, Customization | undefined>;

export const usePutCustomization = (options?: PutCustomizationOptions) => {
  const queryClient = useQueryClient();

  const mutationKey = [CUSTOMIZATION_QUERY_KEY, PUT_KEY];

  return useMutation({
    mutationKey,
    mutationFn: putCustomization,
    onMutate: async (updatedCustomization, context) => {
      const queryKey = [CUSTOMIZATION_QUERY_KEY, LIST_KEY, updatedCustomization.companyKey];

      await queryClient.cancelQueries({ queryKey });

      const previousList = queryClient.getQueryData<Customization[]>(queryKey);
      const previousCustomization = previousList?.find(({ key }) => updatedCustomization.key === key);

      if (previousCustomization) {
        queryClient.setQueryData<Customization[]>(queryKey, (prev) =>
          prev?.map((customization) =>
            customization.key === previousCustomization.key
              ? { ...customization, ...updatedCustomization }
              : customization
          )
        );
      }

      options?.onMutate?.(updatedCustomization, context);

      return previousCustomization;
    },
    onError: (error, variables, onMutateResult, context) => {
      const queryKey = [CUSTOMIZATION_QUERY_KEY, LIST_KEY, variables.companyKey];

      if (onMutateResult) {
        queryClient.setQueryData<Customization[]>(queryKey, (prev) =>
          prev?.map((customization) => (customization.key === onMutateResult.key ? onMutateResult : customization))
        );
      }

      options?.onError?.(error, variables, onMutateResult, context);

      notify.error(error.message);
    },
    onSettled: (queryData, error, variables, onMutateResult, context) => {
      const queryKey = [CUSTOMIZATION_QUERY_KEY, LIST_KEY, variables.companyKey];

      invalidateIfAllMutationsDone(queryKey, mutationKey);

      options?.onSettled?.(queryData, error, variables, onMutateResult, context);
    },
    ...options,
  });
};

const postCustomization = async (customization: Omit<Customization, 'key'>) =>
  await queryYemboApiCallWithReturnValue<Customization, 'customization'>({
    url,
    method: 'POST',
    key: 'customization',
    body: JSON.stringify(customization),
  });

type PostCustomizationOptions = UseMutationOptions<Customization | null, unknown, Omit<Customization, 'key'>>;

export const usePostCustomization = (options?: PostCustomizationOptions) => {
  const mutationKey = [CUSTOMIZATION_QUERY_KEY, POST_KEY];

  return useMutation({
    mutationKey,
    mutationFn: postCustomization,
    onSettled: (data, error, variables, onMutateResult, context) => {
      const queryKey = [CUSTOMIZATION_QUERY_KEY, LIST_KEY, variables.companyKey];

      invalidateIfAllMutationsDone(queryKey, mutationKey);

      options?.onSettled?.(data, error, variables, onMutateResult, context);
    },
    ...options,
  });
};
